import React, { useMemo, useState } from 'react'
import L from 'leaflet'
import { Navigation, MapPin, Check, X, Trash2, Link2, Unlink, Crosshair } from 'lucide-react'
import api from '../api'
import { Button, Input, Select, Field, Badge, Modal, ModalHeader, Alert } from './ui'

// Bagian "jaringan" Peta Pelanggan (fase 2, 2026-09-20): ikon titik OLT/ODC/ODP di peta, kartu
// detail/edit titik, dan modal tautan pelanggan -> ODP. Dipisah dari pages/PetaPelanggan.jsx supaya
// halaman utama (yg mengurus objek Leaflet & state peta) tidak membengkak.

export const LABEL_TIPE = { olt: 'OLT', odc: 'ODC', odp: 'ODP' }
export const WARNA_NODE = { olt: '#0f172a', odc: '#7c3aed', odp: '#2563eb' }
export const WARNA_ODP_PENUH = '#dc2626'

// Jarak garis lurus (meter) -- utk mengurutkan ODP terdekat saat menautkan pelanggan.
export function jarakMeter(a, b) {
  const rad = d => d * Math.PI / 180
  const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2
  return 6371000 * 2 * Math.asin(Math.sqrt(h))
}

export function formatJarak(m) {
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`
}

// Bentuk beda per tipe supaya terbaca tanpa legenda & tidak tertukar dgn pin pelanggan (bulat):
// OLT = kotak gelap, ODC = segi enam ungu, ODP = pil biru berisi "terpakai/kapasitas" (merah kalau penuh).
export function nodeIcon(tipe, { teks, penuh, dipilih } = {}) {
  const ring = dipilih ? 'box-shadow:0 0 0 3px rgba(255,255,255,.95),0 0 0 5px #111;' : 'box-shadow:0 1px 4px rgba(0,0,0,.5);'
  const dasar = 'display:flex;align-items:center;justify-content:center;color:#fff;font:700 11px system-ui,sans-serif;'
  if (tipe === 'odp') {
    const bg = penuh ? WARNA_ODP_PENUH : WARNA_NODE.odp
    return L.divIcon({
      className: '', iconSize: [44, 22], iconAnchor: [22, 11],
      html: `<div style="${dasar}width:44px;height:22px;border-radius:11px;border:2px solid #fff;background:${bg};${ring}">${teks || 'ODP'}</div>`
    })
  }
  if (tipe === 'odc') {
    return L.divIcon({
      className: '', iconSize: [38, 34], iconAnchor: [19, 17],
      html: `<div style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.5))${dipilih ? ' drop-shadow(0 0 3px #111) drop-shadow(0 0 3px #111)' : ''}"><div style="${dasar}width:38px;height:34px;background:${WARNA_NODE.odc};clip-path:polygon(25% 3%,75% 3%,100% 50%,75% 97%,25% 97%,0 50%)">${teks || 'ODC'}</div></div>`
    })
  }
  return L.divIcon({
    className: '', iconSize: [36, 36], iconAnchor: [18, 18],
    html: `<div style="${dasar}width:36px;height:36px;border-radius:9px;border:2px solid #fff;background:${WARNA_NODE.olt};${ring}">${teks || 'OLT'}</div>`
  })
}

function TombolTutup({ onClick }) {
  return <button onClick={onClick} aria-label="Tutup" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-faint)', padding: 2 }}><X size={16} /></button>
}

// Kartu form saat membuat/mengubah ODC/ODP (pin di peta ikut digeser), atau cuma geser pin OLT.
// State form dipegang induk (editing.form) supaya pin yg digeser & isian form tersimpan bersama.
export function KartuEditNode({ editing, setEditing, jaringan, saving, onSimpan, onBatal }) {
  const { tipe, key, form = {} } = editing
  const baru = key === null
  const set = (k, v) => setEditing({ ...editing, form: { ...form, [k]: v } })
  const nodeAsal = !baru && jaringan[tipe] ? jaringan[tipe].find(n => n.id === key) : null
  const judul = tipe === 'olt'
    ? `Atur lokasi OLT: ${nodeAsal ? nodeAsal.nama : ''}`
    : baru ? `${LABEL_TIPE[tipe]} baru` : `Ubah ${LABEL_TIPE[tipe]}: ${nodeAsal ? nodeAsal.nama : ''}`

  return (
    <div className="peta-overlay-card" style={{ borderColor: 'var(--brand)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13.5 }}>
        <Crosshair size={16} color="var(--brand)" />{judul}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 10px' }}>
        Geser pin, atau klik titik di peta. {editing.lat.toFixed(6)}, {editing.lng.toFixed(6)}
      </div>
      {tipe !== 'olt' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <Field label="Nama" style={{ gridColumn: '1 / -1' }}>
            <Input value={form.nama || ''} onChange={e => set('nama', e.target.value)} placeholder={tipe === 'odc' ? 'mis. ODC-Pasar' : 'mis. ODP-Pasar-01'} maxLength={60} />
          </Field>
          {tipe === 'odc' ? (
            <>
              <Field label="OLT induk">
                <Select value={form.olt_id ?? ''} onChange={e => set('olt_id', e.target.value)}>
                  <option value="">Belum diketahui</option>
                  {jaringan.olt.map(o => <option key={o.id} value={o.id}>{o.nama}</option>)}
                </Select>
              </Field>
              <Field label="Port PON">
                <Input value={form.olt_pon || ''} onChange={e => set('olt_pon', e.target.value)} placeholder="mis. PON 1" maxLength={30} />
              </Field>
            </>
          ) : (
            <>
              <Field label="ODC induk">
                <Select value={form.odc_id ?? ''} onChange={e => set('odc_id', e.target.value)}>
                  <option value="">Belum diketahui</option>
                  {jaringan.odc.map(c => <option key={c.id} value={c.id}>{c.nama}</option>)}
                </Select>
              </Field>
              <Field label="Kapasitas port">
                <Input type="number" min={1} max={144} value={form.kapasitas ?? ''} onChange={e => set('kapasitas', e.target.value)} />
              </Field>
            </>
          )}
          <Field label="Keterangan" style={{ gridColumn: '1 / -1' }}>
            <Input value={form.keterangan || ''} onChange={e => set('keterangan', e.target.value)} placeholder="opsional, mis. di tiang depan warung" maxLength={200} />
          </Field>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button size="sm" icon={Check} loading={saving} onClick={onSimpan}>Simpan</Button>
        <Button size="sm" variant="soft" icon={X} disabled={saving} onClick={onBatal}>Batal</Button>
      </div>
    </div>
  )
}

// Kartu detail titik jaringan yg diklik. Daftar pelanggan ODP bisa diklik -> fokus ke pelanggan itu.
export function KartuNode({ tipe, node, jaringan, items, admin, onTutup, onUbah, onHapus, onPilihPelanggan }) {
  const odcInduk = tipe === 'odp' && node.odc_id ? jaringan.odc.find(c => c.id === node.odc_id) : null
  const oltInduk = tipe === 'odc' && node.olt_id ? jaringan.olt.find(o => o.id === node.olt_id) : null
  const anak = useMemo(() => {
    if (tipe === 'odp') return items.filter(i => i.odp_id === node.id).sort((a, b) => (a.odp_port || 999) - (b.odp_port || 999))
    return []
  }, [tipe, node.id, items])
  const jumlahOdp = tipe === 'odc' ? jaringan.odp.filter(p => p.odc_id === node.id).length : 0

  return (
    <div className="peta-overlay-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14.5 }}>{node.nama}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{LABEL_TIPE[tipe]}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {tipe === 'odp' && <Badge tone={node.terpakai >= node.kapasitas ? 'danger' : 'brand'}>{node.terpakai}/{node.kapasitas} port</Badge>}
          <TombolTutup onClick={onTutup} />
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 10px', lineHeight: 1.5 }}>
        {tipe === 'odp' && <div>ODC induk: {odcInduk ? odcInduk.nama : 'belum diketahui'}</div>}
        {tipe === 'odc' && <div>OLT induk: {oltInduk ? `${oltInduk.nama}${node.olt_pon ? ` (${node.olt_pon})` : ''}` : 'belum diketahui'} · {jumlahOdp} ODP</div>}
        {tipe === 'olt' && <div>Konfigurasi & kredensial OLT diatur di halaman Perangkat OLT.</div>}
        {node.keterangan && <div>{node.keterangan}</div>}
        {tipe === 'odp' && (
          anak.length === 0 ? <div>Belum ada pelanggan tertaut.</div> : (
            <div style={{ maxHeight: 92, overflowY: 'auto', marginTop: 4, border: '1px solid var(--border)', borderRadius: 8 }}>
              {anak.map(p => (
                <button key={p.ppp_username} onClick={() => onPilihPelanggan(p)} style={{
                  display: 'flex', gap: 8, width: '100%', padding: '4px 8px', border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 12, textAlign: 'left', color: 'var(--text)', fontFamily: 'inherit'
                }}>
                  <span style={{ width: 52, color: 'var(--text-muted)', flexShrink: 0 }}>{p.odp_port ? `Port ${p.odp_port}` : 'Port ?'}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nama || p.ppp_username}</span>
                </button>
              ))}
            </div>
          )
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {node.lat !== null && (
          <a href={`https://www.google.com/maps/dir/?api=1&destination=${node.lat},${node.lng}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <Button size="sm" icon={Navigation} type="button">Navigasi</Button>
          </a>
        )}
        <Button size="sm" variant="soft" icon={MapPin} onClick={onUbah}>{tipe === 'olt' ? 'Ubah Lokasi' : 'Ubah'}</Button>
        {admin && tipe !== 'olt' && <Button size="sm" variant="soft" icon={Trash2} onClick={onHapus}>Hapus</Button>}
      </div>
    </div>
  )
}

// Modal tautan pelanggan -> ODP. ODP diurutkan dari yg terdekat kalau lokasi pelanggan valid;
// pilihan port cuma menampilkan port yg masih kosong (+ port milik pelanggan ini sendiri).
export function TautanOdpModal({ pelanggan, jaringan, items, onClose, onSimpan }) {
  const [odpId, setOdpId] = useState(pelanggan.odp_id ? String(pelanggan.odp_id) : '')
  const [port, setPort] = useState(pelanggan.odp_port ? String(pelanggan.odp_port) : '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const ada = pelanggan.lat !== null && !pelanggan.mencurigakan
  const daftarOdp = useMemo(() => {
    const arr = jaringan.odp.map(p => ({
      ...p, jarak: ada && p.lat !== null ? jarakMeter({ lat: pelanggan.lat, lng: pelanggan.lng }, p) : null
    }))
    return arr.sort((a, b) => (a.jarak ?? Infinity) - (b.jarak ?? Infinity) || a.nama.localeCompare(b.nama))
  }, [jaringan.odp, ada, pelanggan.lat, pelanggan.lng])

  const odp = jaringan.odp.find(p => String(p.id) === odpId)
  const portTerpakai = useMemo(() => new Set(
    items.filter(i => i.odp_id === (odp && odp.id) && i.odp_port && i.ppp_username !== pelanggan.ppp_username).map(i => i.odp_port)
  ), [items, odp, pelanggan.ppp_username])
  const portBebas = odp ? Array.from({ length: odp.kapasitas }, (_, i) => i + 1).filter(n => !portTerpakai.has(n)) : []

  async function simpan(lepas) {
    setSaving(true); setError('')
    try {
      await api.put(`/peta-jaringan/pelanggan/${encodeURIComponent(pelanggan.ppp_username)}/odp`, {
        odp_id: lepas ? null : odpId || null, odp_port: lepas ? null : port || null
      })
      await onSimpan(lepas ? `Tautan ODP ${pelanggan.nama || pelanggan.ppp_username} dilepas.` : `${pelanggan.nama || pelanggan.ppp_username} ditautkan ke ${odp ? odp.nama : 'ODP'}.`)
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan tautan')
      setSaving(false)
    }
  }

  return (
    <Modal onClose={saving ? undefined : onClose} width={400}>
      <ModalHeader title="Tautkan ke ODP" subtitle={`${pelanggan.nama || pelanggan.ppp_username}${ada ? '' : ' · lokasi pelanggan belum valid, urut nama'}`} onClose={saving ? undefined : onClose} />
      {error && <Alert type="error">{error}</Alert>}
      {jaringan.odp.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>Belum ada ODP. Buat dulu di tab Jaringan.</div>
      ) : (
        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <Field label="ODP">
            <Select value={odpId} onChange={e => { setOdpId(e.target.value); setPort('') }}>
              <option value="">Pilih ODP…</option>
              {daftarOdp.map(p => (
                <option key={p.id} value={p.id} disabled={p.terpakai >= p.kapasitas && p.id !== pelanggan.odp_id}>
                  {p.nama} · {p.terpakai}/{p.kapasitas}{p.terpakai >= p.kapasitas ? ' (penuh)' : ''}{p.jarak !== null ? ` · ${formatJarak(p.jarak)}` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Port">
            <Select value={port} onChange={e => setPort(e.target.value)} disabled={!odp}>
              <option value="">Belum tahu nomor port</option>
              {portBebas.map(n => <option key={n} value={n}>Port {n}</option>)}
            </Select>
          </Field>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button icon={Link2} loading={saving} disabled={!odpId} onClick={() => simpan(false)}>Simpan</Button>
        {pelanggan.odp_id && <Button variant="soft" icon={Unlink} disabled={saving} onClick={() => simpan(true)}>Lepas tautan</Button>}
        <Button variant="soft" disabled={saving} onClick={onClose}>Batal</Button>
      </div>
    </Modal>
  )
}
